import type {
  BillboardSlot,
  BillboardSlotId,
} from "../../services/billboard-api/BillboardApi";

export type BillboardDisplaySnapshot = {
  slotId: BillboardSlotId;
  cadenceLabel: string;
  placementId: string | null;
  message: string;
  destinationUrl: string | null;
  isHouseAd: boolean;
};

const ALLOWED_MESSAGE_CHARACTER =
  /^[\p{L}\p{M}\p{N} .,!?;:'’"“”()[\]{}&@#+/–—-]+$/u;
const URL_SHORTENERS = new Set([
  "bit.ly",
  "buff.ly",
  "cutt.ly",
  "goo.gl",
  "is.gd",
  "ow.ly",
  "rebrand.ly",
  "shorturl.at",
  "t.co",
  "tiny.cc",
  "tinyurl.com",
]);

const HOUSE_ADS: Record<BillboardSlotId, string> = {
  hourly: "YOU ARE\nTRAFFIC.",
  daily: "SIX LANES.\nZERO PROGRESS.",
  forever: "THE INTERNET\nWAS A MISTAKE.",
};

export const BILLBOARD_CADENCE_LABELS: Record<BillboardSlotId, string> = {
  hourly: "1 HOUR",
  daily: "1 DAY",
  forever: "FOREVER",
};

export function currentBillboardPriceCents(
  slot: BillboardSlot,
  serverOffsetMs: number,
  now = Date.now(),
): number {
  if (slot.id === "forever") return Math.max(100, slot.anchorPriceCents);
  const anchorAt = new Date(slot.anchorAt).getTime();
  const serverNow = now + serverOffsetMs;
  if (!Number.isFinite(anchorAt) || !Number.isFinite(serverNow)) {
    return slot.currentPriceCents;
  }
  const interval = slot.id === "hourly" ? 3_600_000 : 86_400_000;
  const elapsedIntervals = Math.floor(
    Math.max(0, serverNow - anchorAt) / interval,
  );
  return Math.max(100, slot.anchorPriceCents - elapsedIntervals * 100);
}

export function nextBillboardDecayAt(
  slot: BillboardSlot,
  serverOffsetMs: number,
): number | null {
  if (slot.id === "forever") return null;
  const anchorAt = new Date(slot.anchorAt).getTime();
  if (!Number.isFinite(anchorAt)) return null;
  const interval = slot.id === "hourly" ? 3_600_000 : 86_400_000;
  const serverNow = Date.now() + serverOffsetMs;
  const intervals = Math.floor(Math.max(0, serverNow - anchorAt) / interval);
  return anchorAt + (intervals + 1) * interval - serverOffsetMs;
}

export function billboardDisplaySnapshots(
  slots: readonly BillboardSlot[],
): BillboardDisplaySnapshot[] {
  const byId = new Map(slots.map((slot) => [slot.id, slot]));
  return (["hourly", "daily", "forever"] as const).map((slotId) => {
    const placement = byId.get(slotId)?.placement;
    return {
      slotId,
      cadenceLabel: BILLBOARD_CADENCE_LABELS[slotId],
      placementId: placement?.id ?? null,
      message: placement?.message ?? HOUSE_ADS[slotId],
      destinationUrl: placement?.destinationUrl ?? null,
      isHouseAd: !placement,
    };
  });
}

export function billboardGraphemeCount(value: string): number {
  return Array.from(
    new Intl.Segmenter("en", { granularity: "grapheme" }).segment(value),
  ).length;
}

export function billboardMessageError(value: string): string | null {
  const normalized = value.normalize("NFKC").replace(/\r\n?/g, "\n");
  if (!normalized.trim()) return "Enter a billboard message.";
  if (normalized.split("\n").length > 2) {
    return "Use at most two lines.";
  }
  if (billboardGraphemeCount(normalized.trim()) > 48) {
    return "Use 48 characters or fewer.";
  }
  if (!ALLOWED_MESSAGE_CHARACTER.test(normalized.replace("\n", " "))) {
    return "Use letters, numbers, spaces, and standard punctuation only.";
  }
  return null;
}

export function billboardDestinationError(value: string): string | null {
  if (!value.trim()) return null;
  if (value.trim().length > 500) return "Use a URL 500 characters or fewer.";
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase().replace(/\.$/, "");
    const looksLikeIp =
      /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":");
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.port ||
      !hostname.includes(".") ||
      hostname === "localhost" ||
      hostname.endsWith(".localhost") ||
      looksLikeIp ||
      URL_SHORTENERS.has(hostname) ||
      Array.from(URL_SHORTENERS).some((shortener) =>
        hostname.endsWith(`.${shortener}`),
      )
    ) {
      return "Use a full public HTTPS URL.";
    }
  } catch {
    return "Use a full public HTTPS URL.";
  }
  return null;
}

export function billboardDestinationDomain(
  destinationUrl: string | null,
): string | null {
  if (!destinationUrl) return null;
  try {
    return new URL(destinationUrl).hostname;
  } catch {
    return null;
  }
}
